// AD (02.11.2026): RimoCompta's menu bar quick menu (shared/tray, design „Tray Menu“). User decisions 01.11.2026: ONE tray
// icon – it opens this menu; „Bezahlung“ switches the Pay-Assistant, which opens in its own window; the global shortcut
// ⌃⌥C (Ctrl+Alt+C) opens the menu and can be switched off. The tray window has no session of its own: the main window
// sends what it shows (`tray:state`) when the menu opens and does what an entry asks (`tray:action`, `tray:suite`).
import { useEffect, useState } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { emit, listen } from '@tauri-apps/api/event';
import { TrayMenu, type TraySuiteEntry } from '@rimocore/shared/tray/TrayMenu';
import { api, API_URL } from '../api/client';
import { displayName, initials, session } from '../api/auth';
import { isFr } from '../i18n/lang';
import { loadApps, openSuiteApp } from '../suiteSwitcher';
import { isDesktop } from '../platform';
import { EV } from './bridge';

export const TRAY_EV = { opened: 'tray:opened', state: 'tray:state', action: 'tray:action', suite: 'tray:suite' } as const;
const GLOBAL_KEY = 'rimocompta.trayGlobalKey';

interface TrayState { user: { name: string; initials: string; color?: string }; online: boolean; host: string; lang: 'de' | 'fr'; suite: TraySuiteEntry[]; payOn: boolean; globalShortcut: boolean }
const short = (key: string) => key.replace(/^rimo/, '');

/** The global shortcut setting (default on; Préférences can switch it off – kept per device). */
export function trayGlobalKey(): boolean { try { return localStorage.getItem(GLOBAL_KEY) !== '0'; } catch { return true; } }
export function setTrayGlobalKey(on: boolean) {
  try { localStorage.setItem(GLOBAL_KEY, on ? '1' : '0'); } catch { /* per-device convenience only */ }
  void invoke('tray_global_key', { on });
}

/**
 * Main window: answers the tray menu. `run(id)` does an entry's action in the main window (the logic's own handlers).
 */
export function serveTray(run: (id: string) => void): () => void {
  if (!isDesktop()) return () => {};
  void invoke('tray_global_key', { on: trayGlobalKey() });
  const offs = [
    listen(TRAY_EV.opened, async () => {
      const u = session.user, online = await api('/config').then(() => true, () => false);
      const apps = online ? await loadApps().catch(() => []) : [];
      const payOn = await invoke<boolean>('pa_active').catch(() => false);
      const state: TrayState = {
        user: { name: displayName(u), initials: initials(u), color: (u as { color?: string } | null)?.color },
        online, host: new URL(API_URL).hostname, lang: isFr() ? 'fr' : 'de',
        suite: [{ key: 'compta', name: 'RimoCompta', src: '/assets/suite/rimocompta.svg', running: true },
          ...apps.filter((a) => a.key !== 'rimocompta').map((a) => ({ key: short(a.key), name: a.name, src: `/assets/suite/${a.key}.svg` }))],
        payOn, globalShortcut: trayGlobalKey(),
      };
      await emit(TRAY_EV.state, state);
    }),
    listen<{ id: string }>(TRAY_EV.action, (e) => run(e.payload.id)),
    listen<{ key: string }>(TRAY_EV.suite, async (e) => { const app = (await loadApps().catch(() => [])).find((a) => short(a.key) === e.payload.key); if (app) await openSuiteApp(app); }),
  ];
  return () => offs.forEach((p) => void p.then((un) => un()));
}

/** The tray window (`#/tray`): the quick menu, filled by the main window. */
export function TrayWindow() {
  const [s, setS] = useState<TrayState | null>(null);
  useEffect(() => {
    document.documentElement.style.background = 'transparent'; document.body.style.background = 'transparent';
    const offs = [
      listen<TrayState>(TRAY_EV.state, (e) => setS(e.payload)),
      listen<boolean>(EV.active, (e) => setS((x) => x && { ...x, payOn: e.payload })),
      // Rust shows the window again: ask for fresh data.
      listen('tray:reopen', () => void emit(TRAY_EV.opened)),
    ];
    void emit(TRAY_EV.opened);
    return () => offs.forEach((p) => void p.then((un) => un()));
  }, []);
  if (!s) return null;
  const hide = () => void invoke('tray_hide');
  return (
    // data-no-tr: the menu carries its own DE/FR texts (design); the app's word replacement stays out.
    <div data-no-tr style={{ padding: '0 10px 24px' }}>
      <TrayMenu app="compta" lang={s.lang} platform={navigator.userAgent.includes('Windows') ? 'win' : 'mac'} online={s.online} user={s.user} host={s.host}
        mark="/assets/rimocompta-mark-dunkel.svg" suite={s.suite} payOn={s.payOn} globalShortcut={s.globalShortcut}
        onPayChange={(on) => { hide(); void invoke('pa_set_active', { on }); }}
        onAction={(id) => { hide(); if (id !== 'close') { void invoke('tray_show_main'); void emit(TRAY_EV.action, { id }); } }}
        onSuite={(key) => { hide(); void emit(TRAY_EV.suite, { key }); }}
        onShowWindow={() => { hide(); void invoke('tray_show_main'); }}
        onQuit={() => void invoke('tray_quit')} />
    </div>
  );
}
